import React, { Fragment, useEffect, useState } from "react";
import PageTitle from "Components/PageTitle/PageTitle";
import MuiPaper from "Components/MuiPaper/MuiPaper";
import Translator from "Languages/Utility/Translator";
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import { useSelector } from "react-redux";
import { push } from "Helpers/History";
import { useSetState } from "Helpers/Hooks";
import Grid from "@material-ui/core/Grid";
import Input from "Components/Input/Input";
import MuiRadioGroup from "Components/MuiRadioGroup/MuiRadioGroup";
import MuiButton from "Components/MuiButton/MuiButton";
import { read } from "Helpers/LocalStorage";
import StatusHandler from "Helpers/StatusHandler";
import ProjectService from "Services/http/endpoints/project/ProjectService";
import { Pie } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTranslation } from "react-i18next";
import { Alert } from "@material-ui/lab";
import { ReactSVG } from "react-svg";
import infoIcon from "Assets/svg/icon/info.svg";

function BenefitInfo(props) {
  const { t } = useTranslation();
  const { benefitData, benefitInfo, selectedProject } = useSelector(
    (state) => ({
      benefitInfo: state.project.benefitInfo,
      benefitData: state.project.benefitData,
      selectedProject: state.project.selectedProject,
    })
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState({});
  const [dataRadio, setDataRadio] = useState({});
  const [dataPie, setDataPie] = useState(null);
  const [priceFactor, setPriceFactor] = useState(0);
  
  const [controls, setControls] = useSetState({
    amountType: {
      type: "radio",
      value: {},
      items: [],
    },
    installationCost: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    otherCost: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    consumedMaterialCost: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    arcCost: {
      type: "price",
      value: 0,
      validation: {
        required: true,
        isPrice: true,
      },
    },
    angleCost: {
      type: "price",
      value: 0,
      validation: {
        required: true,
        isPrice: true,
      },
    },
    tax: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    profitProfile: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    profitFitting: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    profitGlass: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    profitNet: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    profitOther: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
    profitService: {
      type: "percent",
      value: 0,
      validation: {
        required: true,
        isPercent: true,
      },
    },
  });

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

  ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

  const optionsPie = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: 8,
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            let sum = 0;
            let dataArr = context.dataset.data;
            dataArr.map((data) => (sum += data));
            return (
              context.label +
              ":" +
              ((context.raw * 100) / sum).toFixed(1) +
              "% - " +
              context.raw.toLocaleString() +
              t("moneyUnit")
            );
          },
        },
      },
      legend: {
        rtl: true,
        position: "right",
        labels: {
          font: {
            family: "IRANSans",
            size: 10,
          },
        },
      },
      title: {
        display: true,
        text: t("pnlChart"),
        font: {
          family: "IRANSans",
          size: 12,
        },
      },
      datalabels: {
        color: "white",
        font: {
          weight: "bold",
          size: 10,
        },
        display: "auto",
        clip: true,
        textAlign: "center",
        formatter: (value, ctx) => {
          let sum = 0;
          let dataArr = ctx.chart.data.datasets[0].data;
          dataArr.map((data) => (sum += data));
          return ((value * 100) / sum).toFixed(1) + "%";
        },
      },
    },
  };

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const onChangeRadio = (name, key, value) => {
    setControls({ [name]: { value: value } });
    setControls({
      amountType: {
        type: "radio",
        items: dataRadio,
        value: value,
      },
    });
  };

  const onClear = () => {
    let items = [];
    let item = {};
    if (benefitInfo !== null) {
      if (benefitInfo.IsShowPriceUser) {
        item = { value: "User", label: t("user") };
        items.push(item);
      }
      if (benefitInfo.IsShowPriceGroup) {
        item = { value: "Group", label: t("financial_group_admin") };
        items.push(item);
      }
      if (benefitInfo.IsShowPriceAdmin) {
        item = { value: "Admin", label: t("admin") };
        items.push(item);
      }
      setControls({
        amountType: {
          type: "radio",
          items: items,
          value: item,
        },
      });
      setDataRadio(items);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const {
      amountType,
      installationCost,
      otherCost,
      consumedMaterialCost,
      tax,
      profitProfile,
      profitFitting,
      profitGlass,
      profitNet,
      profitOther,
      profitService,
      arcCost,
      angleCost,
    } = controls;
    const params = {
      KeyGroup: read("groupKey"),
      KeyUser: read("userKey"),
      ProjectId: selectedProject.Id,
      FactorId: data.Id,
      TypePure: amountType.value.value,
      CostNasb: installationCost.value,
      CostMasrafi: consumedMaterialCost.value,
      CostJanebi: otherCost.value,
      CostArc: arcCost.value,
      CostAngle: angleCost.value,
      Tax: tax.value,
      ProfileProfit: profitProfile.value,
      FittingProfit: profitFitting.value,
      GlassProfit: profitGlass.value,
      NetProfit: profitNet.value,
      OtherProfit: profitOther.value,
      ServiceProfit: profitService.value,
    };
    const response = await ProjectService.getCalculationProfit(params);
    let err = StatusHandler(response);
    if (!err.isFail) {
      const options = response.data.Value.Pie;
      setDataPie(options);
    } else {
      setDataPie(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    onClear();
  }, []);

  useEffect(() => {
    const benefitId = props.location.pathname.split(
      "/dashboard/projects/profit/"
    )[1];
    if (benefitData?.length > 0 && benefitInfo) {
      const details = benefitData.find(
        (item) => item.Id.toString() === benefitId
      );
      setData(details);
    } else {
      push("/dashboard/projects/profit");
    }
  }, [props.location.pathname]);

  useEffect(() => {
    if (data) {
      setPriceFactor(data.PriceFactor.toLocaleString());
    }
  }, [data]);

  const onCancel = () => {
    push("/dashboard/profit");
  };

  return (
    <Fragment>
      <BackDropLoading loading={loading} />
      <div className="d-flex justify-content-between my-2">
        <PageTitle title={<Translator id="calculation_of_profit_and_loss" />} />
      </div>
      <Alert
        severity="info"
        variant="outlined"
        icon={<ReactSVG src={infoIcon} />}
        className="mb-2"
        style={{ width: "fit-content" }}
      >
        {`${t("price_factor")} : ${priceFactor} ${t(
          "moneyUnit"
        )}`}
      </Alert>
      <MuiPaper>
        <Grid container spacing={2}>
          {Object.keys(controls).map((key, index) => {
            return (
              <Grid item
                lg={controls[key].type === "radio" ? 12 : 2} 
                md={controls[key].type === "radio" ? 12 : 3}
                sm={controls[key].type === "radio" ? 12 : 4} 
                xs={controls[key].type === "radio" ? 12 : 6} 
                key={`item-${key}-${index}`}>
                {
                  controls[key].type === "radio" && (
                  <MuiRadioGroup
                  name={key}
                  value={controls[key].value}
                  items={controls[key].items}
                  onChange={onChangeRadio}
                  className="horizontalRadio"
                  label={<Translator id={"calculate_based"} />}
                />)
                }
                {controls[key].type === "percent" && (
                  <Input
                    setValue={setValue.bind(this)}
                    name={key}
                    validation={controls[key].validation}
                    label={<Translator id={key} />}
                    type={controls[key].type}
                    endIcon="%"
                  />
                )}
                {controls[key].type === "price" && (
                  <Input
                    setValue={setValue.bind(this)}
                    name={key}
                    validation={controls[key].validation}
                    type={controls[key].type}
                    label={<Translator id={key} />}
                    endIcon={<Translator id="moneyUnit" />}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
        <Grid className="mt-3 mb-2">
          <MuiButton
            color="primary"
            onClick={onSubmit}
            title={<Translator id="calculation_of_profit_and_loss" />}
            fullwidthOnSm
          />
        </Grid>
      </MuiPaper>

      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12} md={12}>
          {dataPie && (
            <MuiPaper className="h-100">
              <Pie
                data={dataPie}
                options={optionsPie}
                type="pie"
                style={{ maxHeight: "400px" }}
              />
              <Grid>
                <MuiButton
                  variant="contained"
                  className="m-2"
                  onClick={onCancel}
                  title={<Translator id="enseraf" />}
                />
              </Grid>
            </MuiPaper>
          )}
        </Grid>
      </Grid>
    </Fragment>
  );
}

export default BenefitInfo;
